import axios from 'axios';
import { useUserStore } from '@/stores/user';
import { signinSilent } from '@/services/auth';
import { ANGSTROM_API_URLS, APP_URLS, AUTH_ERROR_RESPONSES, SIS_API_URLS } from '@/constants';
import { IGameMessage, TMessageType, TUserRole } from '@/types';

const sisApiUrl = APP_URLS.PROD.includes(window.location.origin) ? SIS_API_URLS.PROD : SIS_API_URLS.DEV;
const angstromApiUrl = APP_URLS.PROD.includes(window.location.origin)
  ? ANGSTROM_API_URLS.PROD
  : ANGSTROM_API_URLS.DEV;

export const HEARTBEAT_TIMEOUT_MS = 4000;
export const PLAY_EVENT_TIMEOUT_MS = 20000;
export const GAME_STATE_TIMEOUT_MS = 10000;

// 401 likely means an expired token - try a silent recovery before locking the user out;
// 403/435 are genuine permission failures, so lock out immediately.
export const userNotAuthorized = async (status?: number) => {
  const userStore = useUserStore();

  if (status === 401 && !userStore.isRecoveringAuth) {
    // flip to unauthenticated up front so the UI shows "reconnecting" (not the live app) while retrying;
    // a successful signinSilent() below restores it via setCurrentUser().
    userStore.setAuth(false);
    try {
      await signinSilent();
      return;
    } catch {
      // recovery failed, fall through to lockout below
    }
  }

  userStore.setAuth(false);
};

const generateHeaders = () => {
  const userStore = useUserStore();
  return {
    Authorization: `Bearer ${userStore.currentUser.access_token}`,
    'Content-Type': 'application/json',
  };
};

const withScoutHeader = (payload: IGameMessage): IGameMessage => {
  const userStore = useUserStore();
  return {
    ...payload,
    header: {
      ...payload.header,
      scout: userStore.getUserRole(),
    },
  };
};

export const getAllGames = async (season: string) => {
  try {
    const response = await axios.get(`${sisApiUrl}/api/v2/cfb/schedule/seasons/${season}`, {
      headers: generateHeaders(),
    });
    return response.data;
  } catch (err: any) {
    if (AUTH_ERROR_RESPONSES.includes(err.response?.status)) {
      await userNotAuthorized(err.response.status);
      console.log(`${err.response.status}: Auth issue`);
    }
    return [];
  }
};

export const getPlayerProjections = async (season: string, week: number, teamId: number) => {
  try {
    const response = await axios.get(
      `${sisApiUrl}/api/v2/cfb/projections/seasons/${season}/weeks/${week}/teams/${teamId}/fantasy/projections`,
      {
        headers: generateHeaders(),
      }
    );
    return response.data;
  } catch (err: any) {
    if (AUTH_ERROR_RESPONSES.includes(err.response?.status)) {
      await userNotAuthorized(err.response.status);
      console.log(`${err.response.status}: Auth issue`);
    }
    throw new Error(err.message);
  }
};

export const getTeamRosterInfo = async (season: string, teamId: number) => {
  try {
    const response = await axios.get(
      `${sisApiUrl}/api/v2/cfb/player/seasons/${season}/rosters?teamId=${teamId}`,
      {
        headers: generateHeaders(),
      }
    );
    return response.data;
  } catch (err: any) {
    if (AUTH_ERROR_RESPONSES.includes(err.response?.status)) {
      await userNotAuthorized(err.response.status);
      console.log(`${err.response.status}: Auth issue`);
    }
    throw new Error(err.message);
  }
};

export const getTeamPlayerInfo = async (season: string, teamId: number) => {
  try {
    const response = await axios.get(
      `${sisApiUrl}/api/v2/cfb/player/seasons/${season}/playerInfo?teamId=${teamId}`,
      {
        headers: generateHeaders(),
      }
    );
    return response.data;
  } catch (err: any) {
    if (AUTH_ERROR_RESPONSES.includes(err.response?.status)) {
      await userNotAuthorized(err.response.status);
      console.log(`${err.response.status}: Auth issue`);
    }
    throw new Error(err.message);
  }
};

export const getSnapshot = async (gameId: number) => {
  try {
    const response = await axios.get(`${angstromApiUrl}/games/${gameId}`, {
      headers: generateHeaders(),
    });
    return response.data;
  } catch (err: any) {
    if (err.response?.status === 404) {
      return undefined;
    }
    if (AUTH_ERROR_RESPONSES.includes(err.response?.status)) {
      await userNotAuthorized(err.response.status);
      console.log(`${err.response.status}: Auth issue`);
    } else {
      console.log(err);
    }
    return null;
  }
};

export const sendHeartbeat = async (gameId: number, scout: TUserRole) => {
  const header = {
    messageType: TMessageType.HEARTBEAT,
    scout,
    clientTimestamp: new Date().toISOString(),
  };
  const body = {
    gameId,
  };
  const gameMessage: IGameMessage[] = [
    {
      header,
      body,
    },
  ];
  try {
    return await axios({
      method: 'post',
      url: `${angstromApiUrl}/games/ingest`,
      data: gameMessage,
      headers: generateHeaders(),
      timeout: HEARTBEAT_TIMEOUT_MS,
    });
  } catch (err: any) {
    if (AUTH_ERROR_RESPONSES.includes(err.response?.status)) {
      await userNotAuthorized(err.response.status);
      return false;
    }
    throw new Error(err.message);
  }
};

export const sendUpdate = async (
  gameMessage: IGameMessage,
  isPlayUpdate = false,
  method: 'post' | 'put' = 'post',
  timeout: number = PLAY_EVENT_TIMEOUT_MS
) => {
  const payload = withScoutHeader(gameMessage);

  try {
    if (isPlayUpdate) {
      return await axios({
        method,
        url: `${angstromApiUrl}/games/plays/update?sourceId=1`,
        data: payload,
        headers: generateHeaders(),
        timeout,
      });
    }
    return await axios({
      method,
      url: `${angstromApiUrl}/games/ingest`,
      data: [payload],
      headers: generateHeaders(),
      timeout,
    });
  } catch (err: any) {
    if (AUTH_ERROR_RESPONSES.includes(err.response?.status)) {
      await userNotAuthorized(err.response.status);
      console.log(`${err.response.status}: Auth issue`);
      return false;
    }
    throw new Error(err.message);
  }
};

export const sendBulkUpdate = async (
  gameMessages: IGameMessage[],
  timeout: number = PLAY_EVENT_TIMEOUT_MS
) => {
  const payload = gameMessages.map((gameMessage) => withScoutHeader(gameMessage));

  try {
    return await axios({
      method: 'post',
      url: `${angstromApiUrl}/games/ingest`,
      data: payload,
      headers: generateHeaders(),
      timeout,
    });
  } catch (err: any) {
    if (AUTH_ERROR_RESPONSES.includes(err.response?.status)) {
      await userNotAuthorized(err.response.status);
      console.log(`${err.response.status}: Auth issue`);
      return false;
    }
    throw new Error(err.message);
  }
};
